import { useQuery } from '@tanstack/react-query';
import apis from '@api/api';

export interface DataPaginationOptions {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
}

export interface DataPaginationState {
	currentPage: number;
	pageSize: number;
	sortBy: string;
	sortOrder: 'asc' | 'desc';
}

export interface UseDataQueryOptions {
	selectedId?: string;
	dataFilter?: string;
	pagination?: DataPaginationState;
	enabled?: boolean;
}

/**
 * Custom hook for fetching paginated data content
 * @param options - Query options including pagination state
 * @returns React Query result with data content and pagination info
 */
export const useDataContentQuery = (options: UseDataQueryOptions = {}) => {
	const {
		selectedId,
		pagination = {
			currentPage: 1,
			pageSize: 10,
			sortBy: 'updatedAt',
			sortOrder: 'desc',
		},
		enabled = true,
	} = options;

	return useQuery({
		queryKey: [
			'data-content',
			selectedId,
			pagination.currentPage,
			pagination.pageSize,
			pagination.sortBy,
			pagination.sortOrder,
		],
		queryFn: async () => {
			console.log('useDataContentQuery - Making API call with:', {
				selectedId,
				pagination,
			});

			// Use the new paginated API endpoint
			const data = await apis.getDataContentPaginated(selectedId, {
				page: pagination.currentPage,
				limit: pagination.pageSize,
				sortBy: pagination.sortBy,
				sortOrder: pagination.sortOrder,
			});

			console.log('useDataContentQuery - API response:', {
				status: data.status,
				dataKeys: Object.keys(data.data || {}),
				records: data.data?.data?.length || 0,
				pagination: data.data?.pagination,
			});

			return data.data;
		},
		enabled: enabled && !!selectedId,
		retry: 0,
		refetchOnMount: true,
		refetchOnWindowFocus: true,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

/**
 * Custom hook for fetching paginated data folders
 * @param options - Query options including pagination state
 * @returns React Query result with data folders and pagination info
 */
export const useDataFoldersQuery = (options: UseDataQueryOptions = {}) => {
	const {
		selectedId,
		dataFilter,
		pagination = {
			currentPage: 1,
			pageSize: 10,
			sortBy: 'updatedAt',
			sortOrder: 'desc',
		},
		enabled = true,
	} = options;

	return useQuery({
		queryKey: [
			'data-folders',
			selectedId,
			dataFilter,
			pagination.currentPage,
			pagination.pageSize,
			pagination.sortBy,
			pagination.sortOrder,
		],
		queryFn: async () => {
			// Backend uses companyNumber from session, so we don't need to pass it
			// But we pass selectedId for query key uniqueness and fallback
			// The backend will prioritize session companyNumber over this id
			const data = await apis.getDataFoldersPaginated(selectedId, {
				page: pagination.currentPage,
				limit: pagination.pageSize,
				sortBy: pagination.sortBy,
				sortOrder: pagination.sortOrder,
				dataFilter,
			});
			return data.data;
		},
		enabled: enabled && !!selectedId,
		retry: 0,
		staleTime: 0, // Always refetch to ensure we get latest data from session
		refetchOnMount: true, // Refetch when component mounts to ensure fresh data
		refetchOnWindowFocus: true, // Refetch when window regains focus
	});
};

/**
 * Utility function to create pagination state
 * @param initialState - Initial pagination state
 * @returns Pagination state object
 */
export const createPaginationState = (
	initialState: Partial<DataPaginationState> = {}
): DataPaginationState => ({
	currentPage: 1,
	pageSize: 10,
	sortBy: 'updatedAt',
	sortOrder: 'desc',
	...initialState,
});

/**
 * Utility function to update pagination state
 * @param currentState - Current pagination state
 * @param updates - Updates to apply
 * @returns Updated pagination state
 */
export const updatePaginationState = (
	currentState: DataPaginationState,
	updates: Partial<DataPaginationState>
): DataPaginationState => ({
	...currentState,
	...updates,
});

/**
 * Utility function to reset pagination to first page
 * @param currentState - Current pagination state
 * @returns Updated pagination state with page reset to 1
 */
export const resetPaginationPage = (
	currentState: DataPaginationState
): DataPaginationState => ({
	...currentState,
	currentPage: 1,
});
